import { Link, useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppNavigation } from "../hooks/useAppNavigation";

/**
 * Public footer — clean, no admin/technical links visible.
 * Admin Technique is only reachable via /admin URL for privileged users.
 * Dead-links show a "En préparation" toast so no CTA is ever inert.
 */
export default function PublicFooter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { comingSoon } = useAppNavigation();

  const goSection = (hash) => (e) => {
    e.preventDefault();
    if (window.location.pathname !== "/") {
      navigate(`/${hash}`);
    } else {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const soon = (label) => (e) => {
    e.preventDefault();
    comingSoon(label);
  };

  const openSocial = (platform) => (e) => {
    e.preventDefault();
    comingSoon(`Page ${platform} AFRICTAL`);
  };

  return (
    <>
      <footer className="afr-footer" data-testid="afr-public-footer">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
            <span className="afr-mark" aria-hidden="true">
              <img src="/brand/africtal-logo.png" alt="AFRICTAL" />
            </span>
            <span style={{ fontFamily: "'Outfit',sans-serif", fontWeight: 700, color: "var(--afr-secondary)" }}>
              AFRICTAL
            </span>
          </div>
          <p className="afr-footer__brand-lead">{t("footer.brand_lead")}</p>
          <div className="afr-flex-row">
            <a
              href="mailto:hello@africtal.com"
              className="afr-btn afr-btn--outline afr-btn--sm"
              data-testid="footer-btn-email"
            >
              <Mail size={14} /> hello@africtal.com
            </a>
          </div>
        </div>

        <div>
          <h4>{t("footer.col_product")}</h4>
          <ul>
            <li><a href="/#ecosystemes" onClick={goSection("#ecosystemes")} data-testid="footer-link-ecosystemes">{t("footer.links.ecosystems")}</a></li>
            <li><a href="/#personas" onClick={goSection("#personas")} data-testid="footer-link-personas">{t("footer.links.personas")}</a></li>
            <li><a href="/#coach-ia" onClick={goSection("#coach-ia")} data-testid="footer-link-coach-ia">{t("footer.links.coach_ia")}</a></li>
            <li><Link to="/tarifs" data-testid="footer-link-tarifs">{t("footer.links.pricing")}</Link></li>
            <li><a href="/#comment" onClick={goSection("#comment")} data-testid="footer-link-comment">{t("footer.links.how_it_works")}</a></li>
          </ul>
        </div>

        <div>
          <h4>{t("footer.col_ecosystem")}</h4>
          <ul>
            <li><Link to="/pour-les-talents" data-testid="footer-link-talents">{t("footer.links.talents")}</Link></li>
            <li><Link to="/pour-les-employeurs" data-testid="footer-link-employeurs">{t("footer.links.employeurs")}</Link></li>
            <li><Link to="/pour-les-universites" data-testid="footer-link-universites">{t("footer.links.universites")}</Link></li>
            <li><Link to="/pour-les-investisseurs" data-testid="footer-link-investisseurs">{t("footer.links.investisseurs")}</Link></li>
            <li><a href="/#partenaires" onClick={goSection("#partenaires")} data-testid="footer-link-partenaires">{t("footer.links.partenaires")}</a></li>
          </ul>
        </div>

        <div>
          <h4>{t("footer.col_company")}</h4>
          <ul>
            <li><Link to="/a-propos" data-testid="footer-link-about">{t("footer.links.about")}</Link></li>
            <li><Link to="/a-propos#mission" data-testid="footer-link-mission">{t("footer.links.mission")}</Link></li>
            <li><Link to="/a-propos#valeurs" data-testid="footer-link-valeurs">{t("footer.links.values")}</Link></li>
            <li><a href="mailto:hello@africtal.com" data-testid="footer-link-contact">{t("footer.links.contact")}</a></li>
          </ul>
        </div>

        <div>
          <h4>{t("footer.col_support")}</h4>
          <ul>
            <li><a href="/#faq" onClick={goSection("#faq")} data-testid="footer-link-faq">{t("footer.links.faq")}</a></li>
            <li><a href="mailto:hello@africtal.com" data-testid="footer-link-email">hello@africtal.com</a></li>
            <li><Link to="/login" data-testid="footer-link-login">{t("footer.links.login")}</Link></li>
            <li><Link to="/login?mode=signup" data-testid="footer-link-signup">{t("footer.links.signup")}</Link></li>
          </ul>
        </div>

        <div>
          <h4>{t("footer.col_legal")}</h4>
          <ul>
            <li><a href="#" onClick={soon(t("footer.links.legal"))} data-testid="footer-link-legal">{t("footer.links.legal")}</a></li>
            <li><a href="#" onClick={soon(t("footer.links.privacy"))} data-testid="footer-link-privacy">{t("footer.links.privacy")}</a></li>
            <li><a href="#" onClick={soon(t("footer.links.terms"))} data-testid="footer-link-terms">{t("footer.links.terms")}</a></li>
            <li><a href="#" onClick={soon(t("footer.links.cookies"))} data-testid="footer-link-cookies">{t("footer.links.cookies")}</a></li>
          </ul>
        </div>
      </footer>
      <div
        style={{
          padding: "1.4rem 2rem 2rem",
          borderTop: "1px solid var(--afr-border)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
        data-testid="afr-footer-bottom"
      >
        <span style={{ fontSize: "0.82rem", color: "var(--afr-muted)" }}>
          {t("footer.copyright")}
        </span>
        <div className="afr-flex-row">
          <button type="button" aria-label="LinkedIn" className="afr-topbar__icon" onClick={openSocial("LinkedIn")} data-testid="footer-social-linkedin">
            <span style={{ fontWeight: 700, fontSize: "0.72rem" }}>in</span>
          </button>
          <button type="button" aria-label="Twitter/X" className="afr-topbar__icon" onClick={openSocial("Twitter/X")} data-testid="footer-social-x">
            <span style={{ fontWeight: 700, fontSize: "0.72rem" }}>X</span>
          </button>
          <button type="button" aria-label="YouTube" className="afr-topbar__icon" onClick={openSocial("YouTube")} data-testid="footer-social-youtube">
            <span style={{ fontWeight: 700, fontSize: "0.72rem" }}>▶</span>
          </button>
          <button type="button" aria-label="Instagram" className="afr-topbar__icon" onClick={openSocial("Instagram")} data-testid="footer-social-instagram">
            <span style={{ fontWeight: 700, fontSize: "0.72rem" }}>◎</span>
          </button>
        </div>
      </div>
    </>
  );
}
